'use strict';

/**
 * NACRE Wave 2.4.1 — login-client
 *
 * Browser-side identity-vault unwrap. The server returns the wrapped meta
 * + vaultBin from disk; the browser runs Argon2id locally with the user's
 * password to derive the unwrap key, then decrypts vaultBin to expose the
 * private keys.
 *
 * The server NEVER:
 *   - Computes the unwrap KDF (it has the password but doesn't use it for unwrap)
 *   - Stores the identityMaster
 *   - Has access to the unwrapped private keys
 *
 * The server DOES:
 *   - Verify the user's password against the passwordHash field for AUTH
 *     (separate Argon2id with a different salt — the auth hash and the
 *      unwrap KEK are NOT the same value, even though they share an input)
 *   - Read the wrapped meta + vaultBin from disk and ship them to the browser
 *
 * The two Argon2id outputs (auth hash vs unwrap KEK) use DIFFERENT salts
 * stored in DIFFERENT places, so even though they're both functions of the
 * password, knowing the auth hash gives you nothing toward the unwrap KEK.
 *
 * This module is a pure function — no I/O, no state, no side effects.
 * Designed to be browserifiable. Uses only @noble/* and @scure/* deps via
 * the kdf + symmetric primitives.
 */

const { argon2id } = require('./kdf');
const symmetric = require('./symmetric');
const { deriveAuthProof } = require('./auth-proof');

const REQUIRED_META_FIELDS = [
  'saltPw', 'wrapPw', 'publicKeys',
];

function deserializeFromStorage(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    if (typeof value.__u8 === 'string') {
      return new Uint8Array(Buffer.from(value.__u8, 'hex'));
    }
    const out = {};
    for (const k of Object.keys(value)) {
      out[k] = deserializeFromStorage(value[k]);
    }
    return out;
  }
  if (Array.isArray(value)) {
    return value.map(deserializeFromStorage);
  }
  return value;
}

function validateMeta(meta) {
  if (!meta || typeof meta !== 'object') {
    throw new Error('Invalid meta: must be an object');
  }
  for (const f of REQUIRED_META_FIELDS) {
    if (meta[f] === undefined || meta[f] === null) {
      throw new Error('Invalid meta: missing required field "' + f + '"');
    }
  }
  if (typeof meta.saltPw !== 'string' || meta.saltPw.length < 32) {
    throw new Error('Invalid meta: saltPw must be hex of at least 16 bytes');
  }
  const w = meta.wrapPw;
  if (!w || w.algo === undefined || w.iv === undefined || w.ct === undefined || w.tag === undefined) {
    throw new Error('Invalid meta: wrapPw must be a complete AES-GCM envelope');
  }
}

function validateVaultBin(vaultBin) {
  if (!vaultBin || typeof vaultBin !== 'object') {
    throw new Error('Invalid vaultBin: must be an object');
  }
  if (vaultBin.algo === undefined || vaultBin.iv === undefined ||
      vaultBin.ct === undefined || vaultBin.tag === undefined) {
    throw new Error('Invalid vaultBin: missing envelope fields');
  }
}

/**
 * Unwrap an identity vault using a password.
 *
 * @param {object} args
 * @param {string} args.password
 * @param {object} args.meta — server-supplied identity vault meta
 * @param {object} args.vaultBin — server-supplied identity vault binary
 * @param {object} [args.kdfProfile] — Argon2id parameters (defaults to DESKTOP)
 * @returns {{
 *   publicKeys: object,
 *   privateKeys: object,
 *   moduleMasters: object,
 *   policies: object,
 *   _identityMaster: Uint8Array
 * }}
 */
function unlockIdentityVault({ password, meta, vaultBin, kdfProfile }) {
  if (typeof password !== 'string' || password.length === 0) {
    throw new Error('Invalid password: must be a non-empty string');
  }
  validateMeta(meta);
  validateVaultBin(vaultBin);

  // 1. Derive the unwrap key from the password + saltPw
  const saltPw = Buffer.from(meta.saltPw, 'hex');
  const keyPw = argon2id(password, saltPw, kdfProfile);

  // 2. Unwrap identityMaster from wrapPw
  let identityMaster;
  try {
    identityMaster = symmetric.decrypt(meta.wrapPw, keyPw, { binary: true });
  } catch (err) {
    throw new Error('Wrong password (identityMaster unwrap failed)');
  }

  // 3. Decrypt vaultBin with identityMaster to get the inner contents
  let innerJson;
  try {
    innerJson = symmetric.decrypt(vaultBin, identityMaster);
  } catch (err) {
    throw new Error('Vault binary decryption failed (corrupt or tampered)');
  }
  const inner = deserializeFromStorage(JSON.parse(innerJson));

  return {
    publicKeys: deserializeFromStorage(meta.publicKeys),
    privateKeys: inner.privateKeys,
    moduleMasters: inner.moduleMasters || {},
    policies: inner.policies || {},
    _identityMaster: identityMaster,
  };
}

module.exports = {
  unlockIdentityVault,
  deriveAuthProof,
};
